import BACKEND_URL from 'constants/backendUrl';
import { Civility } from 'core/enums/civility';
import { UserRole } from 'core/enums/userRole';
import Chart from 'images/icons/Chart.svg?react';
import Female from 'images/icons/Female.svg?react';
import File from 'images/icons/File.svg?react';
import Male from 'images/icons/Male.svg?react';
import Network from 'images/icons/Network.svg?react';
import SearchIcon from 'images/icons/Search.svg?react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSessionStore } from 'stores/session';
import { POST } from 'utils/http';
import Button from '../Button';
import MultiActionsButton from '../MultiActionsButton';
import { SLink, SList, SNav } from './style';

interface ActionsProps {
    userRole: UserRole;
}

const logOutFetcher = async () => {
    try {
        await POST(`${BACKEND_URL}/api/auth/logout`);
    } catch (error) {
        throw new Error('UNKNOWN_ERROR');
    }
};

const CandidateActions: FC = () => {
    const { t } = useTranslation();
    return (
        <>
            <li>
                <SLink title={t('header.dashboard')} to="/dashboard">
                    <Chart />
                    <span>{t('header.dashboard')}</span>
                </SLink>
            </li>
            <li>
                <SLink title={t('header.contact')} to="/contacts">
                    <Network />
                    <span>{t('header.contact')}</span>
                </SLink>
            </li>
            <li>
                <SLink title={t('common.activities')} to="/activities">
                    <File />
                    <span>{t('common.activities')}</span>
                </SLink>
            </li>
            <li>
                <SLink title={t('common.applications')} to="/applications">
                    <File />
                    <span>{t('common.applications')}</span>
                </SLink>
            </li>
            <li>
                <SLink title={t('page.global-search.nav-label')} to="/search">
                    <SearchIcon />
                    <span>{t('page.global-search.nav-label')}</span>
                </SLink>
            </li>
        </>
    );
};

const AdminActions: FC = () => {
    const { t } = useTranslation();
    return (
        <>
            <li>
                <SLink title={t('header.candidates')} to="/candidates">
                    <Network />
                    <span>{t('header.candidates')}</span>
                </SLink>
            </li>
            <li>
                <SLink title={t('header.agencies')} to="/agencies">
                    <Network />
                    <span>{t('header.agencies')}</span>
                </SLink>
            </li>
        </>
    );
};

const AgencyAdminActions: FC = () => {
    const { t } = useTranslation();
    return (
        <li>
            <SLink title={t('header.candidates')} to="/candidates">
                <Network />
                <span>{t('header.candidates')}</span>
            </SLink>
        </li>
    );
};

const Actions: FC<ActionsProps> = ({ userRole }) => {
    switch (userRole) {
        case UserRole.Candidate:
            return <CandidateActions />;
        case UserRole.AgencyAdmin:
            return <AgencyAdminActions />;
        case UserRole.Admin:
            return <AdminActions />;
        default:
            throw Error('Wrong user role type enum in header actions');
    }
};

const ConnectedActions: FC = () => {
    const { t } = useTranslation();
    const history = useHistory();

    const user = useSessionStore((s) => s.user);
    const logout = useSessionStore((s) => s.logout);

    if (!user) {
        return null;
    }

    const logOut = async () => {
        try {
            await logOutFetcher();
            logout();
            history.push('/login');
        } catch (error) {
            // maybe store the error in a global state and have a way to display the 'global' errors in every pages
        }
        logout();
    };

    return (
        <SNav>
            <SList>
                <Actions
                    userRole={user.role as UserRole}
                />
                <li>
                    <MultiActionsButton
                        parentButtonContent={Civility.Female === user.civility ? <Female /> : <Male />}
                        iconButton
                    >
                        <Button onClick={() => history.push('/profile')} variant="popinText">
                            {t('profile.label')}
                        </Button>
                        {user.role === UserRole.Candidate && (
                            <Button onClick={() => history.push('/ask-subscription')} variant="popinText">
                                {t('ask-subscription.label')}
                            </Button>
                        )}
                        <Button onClick={() => history.push('/help')} variant="popinText">
                            {t('help.label')}
                        </Button>
                        <Button onClick={() => history.push('/legal-notice')} variant="popinText">
                            {t('legal-notice.label')}
                        </Button>
                        <Button onClick={logOut} variant="popinText">
                            {t('header.logout')}
                        </Button>
                    </MultiActionsButton>
                </li>
            </SList>
        </SNav>
    );
};

export default ConnectedActions;
